import { createFileRoute, Link } from "@tanstack/react-router";
import logoAsset from "@/assets/psy-bloom-logo.png.asset.json";
import { useRouteHead } from "@/hooks/use-route-head";

const routeHead = {
  meta: [
    { title: "Termeni și Condiții | PSY BLOOM Institute" },
    {
      name: "description",
      content:
        "Termenii și condițiile de utilizare a website-ului PSY BLOOM Institute.",
    },
    { property: "og:title", content: "Termeni și Condiții | PSY BLOOM Institute" },
    {
      property: "og:description",
      content:
        "Termenii și condițiile de utilizare a website-ului PSY BLOOM Institute.",
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "/termeni-si-conditii" },
  ],
  links: [{ rel: "canonical", href: "/termeni-si-conditii" }],
};

export const Route = createFileRoute("/termeni-si-conditii")({
  component: TermeniSiConditiiPage,
});

function TermeniSiConditiiPage() {
  useRouteHead(routeHead);
  return (
    <div className="min-h-screen bg-background">
      {/* HEADER */}
      <header className="w-full border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="PSY BLOOM Institute">
            <img src={logoAsset.url} alt="PSY BLOOM Institute" className="h-9 w-auto" width={180} height={36} />
          </Link>
          <a
            href="https://wa.me/40700000000"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-[var(--shadow-card)]"
          >
            Programează o discuție
          </a>
        </div>
      </header>

      <main>
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-6">
            <h1 className="text-4xl md:text-5xl leading-[1.1] mb-6 text-balance">
              Termeni și Condiții
            </h1>
            <p className="text-sm text-muted-foreground mb-12">
              Ultima actualizare: iunie 2026
            </p>

            <div className="space-y-12 text-base md:text-lg text-muted-foreground leading-relaxed">
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  1. Informații generale
                </h2>
                <div className="space-y-4">
                  <p>
                    Website-ul PSY BLOOM Institute este administrat și operat de următoarele forme independente de exercitare a profesiei de psiholog:
                  </p>
                  <div className="pl-4 border-l-2 border-border space-y-2">
                    <p>
                      <strong className="text-foreground">Vlad Ancuța Iulia – Cabinet Individual de Psihologie</strong><br />
                      Cod de înregistrare fiscală: 51414430<br />
                      Sediu profesional: Municipiul București, Str. Stănescu Gheorghe nr. 3
                    </p>
                    <p>
                      <strong className="text-foreground">Popescu Gheorghiu Nicoleta Sorina – Cabinet Individual de Psihologie</strong><br />
                      Cod de înregistrare fiscală: 34780539<br />
                      Sediu profesional: Strada 9 Mai nr. 3, bl. 37, ap. 37, Sector 6, București
                    </p>
                  </div>
                  <p>
                    Prin accesarea și utilizarea website-ului, acceptați termenii și condițiile prezentate în acest document.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  2. Scopul website-ului
                </h2>
                <div className="space-y-4">
                  <p>
                    Website-ul are rol informativ și educațional și oferă informații despre:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>activitatea profesională desfășurată în cadrul PSY BLOOM Institute;</li>
                    <li>serviciile de psihologie și consiliere psihologică oferite de specialiști;</li>
                    <li>articole și materiale educaționale din domeniul psihologiei;</li>
                    <li>posibilitatea transmiterii solicitărilor de contact sau programare.</li>
                  </ul>
                  <p>
                    Informațiile publicate pe website nu reprezintă evaluare psihologică, diagnostic, recomandare medicală sau intervenție terapeutică individuală și nu pot înlocui relația profesională directă dintre psiholog și client.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  3. Serviciile prezentate pe website
                </h2>
                <div className="space-y-4">
                  <p>
                    Serviciile prezentate pe website sunt oferite în conformitate cu legislația română aplicabilă profesiei de psiholog cu drept de liberă practică și cu normele profesionale ale Colegiului Psihologilor din România.
                  </p>
                  <p>
                    Prezentarea serviciilor pe website are caracter informativ și nu constituie o ofertă contractuală fermă.
                  </p>
                  <p>
                    Transmiterea unei solicitări prin formularul de contact, e-mail, telefon sau WhatsApp nu reprezintă o programare confirmată. Programarea este considerată confirmată doar după validarea expresă de către specialistul ales.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  4. Programări și comunicare
                </h2>
                <div className="space-y-4">
                  <p>
                    Persoanele care solicită o programare se obligă să furnizeze informații reale, complete și actualizate.
                  </p>
                  <p>
                    PSY BLOOM Institute își rezervă dreptul de a refuza sau reprograma solicitările în situațiile în care:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>nu există disponibilitate;</li>
                    <li>solicitarea nu intră în aria de competență profesională;</li>
                    <li>există motive obiective care împiedică desfășurarea serviciului în condiții adecvate;</li>
                    <li>comportamentul solicitantului este abuziv, agresiv sau incompatibil cu desfășurarea unei relații profesionale.</li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  5. Drepturi de proprietate intelectuală
                </h2>
                <div className="space-y-4">
                  <p>
                    Întregul conținut al website-ului, incluzând, fără limitare:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>texte;</li>
                    <li>articole;</li>
                    <li>materiale educaționale;</li>
                    <li>imagini;</li>
                    <li>elemente grafice;</li>
                    <li>logo-uri;</li>
                    <li>identitate vizuală;</li>
                    <li>structură și design,</li>
                  </ul>
                  <p>
                    este protejat de legislația privind drepturile de autor și proprietatea intelectuală.
                  </p>
                  <p>
                    Este interzisă copierea, reproducerea, distribuirea, publicarea, modificarea sau utilizarea conținutului fără acordul prealabil scris al titularilor drepturilor.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  6. Utilizarea website-ului
                </h2>
                <p>
                  Utilizatorii se obligă:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>să utilizeze website-ul într-un mod legal și responsabil;</li>
                  <li>să nu încerce accesarea neautorizată a sistemelor informatice;</li>
                  <li>să nu afecteze funcționarea tehnică a website-ului;</li>
                  <li>să nu transmită conținut ilegal, defăimător, ofensator sau discriminatoriu;</li>
                  <li>să nu folosească website-ul în scopuri frauduloase.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  7. Limitarea răspunderii
                </h2>
                <div className="space-y-4">
                  <p>
                    PSY BLOOM Institute depune eforturi rezonabile pentru menținerea acurateței informațiilor publicate.
                  </p>
                  <p>Cu toate acestea:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>nu garantăm că toate informațiile sunt permanent complete, actualizate sau lipsite de erori;</li>
                    <li>nu garantăm disponibilitatea neîntreruptă a website-ului;</li>
                    <li>nu răspundem pentru eventualele prejudicii rezultate din utilizarea sau imposibilitatea utilizării website-ului;</li>
                    <li>nu răspundem pentru deciziile luate exclusiv pe baza informațiilor publicate pe site.</li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  8. Linkuri către website-uri externe
                </h2>
                <div className="space-y-4">
                  <p>
                    Website-ul poate conține linkuri către website-uri administrate de terți.
                  </p>
                  <p>
                    PSY BLOOM Institute nu controlează și nu își asumă responsabilitatea pentru conținutul, politicile sau practicile acestor website-uri externe.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  9. Confidențialitate și protecția datelor
                </h2>
                <div className="space-y-4">
                  <p>
                    Prelucrarea datelor cu caracter personal se realizează în conformitate cu legislația aplicabilă privind protecția datelor și cu prevederile Regulamentului (UE) 2016/679 (GDPR).
                  </p>
                  <p>
                    Detalii complete privind colectarea și prelucrarea datelor sunt disponibile în pagina „Politica de Confidențialitate”.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  10. Modificarea termenilor și condițiilor
                </h2>
                <div className="space-y-4">
                  <p>
                    PSY BLOOM Institute își rezervă dreptul de a modifica oricând prezentul document.
                  </p>
                  <p>
                    Versiunea actualizată va fi publicată pe această pagină, iar modificările vor produce efecte de la data publicării.
                  </p>
                  <p>
                    Utilizarea continuă a website-ului după publicarea modificărilor reprezintă acceptarea noilor termeni și condiții.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  11. Legea aplicabilă
                </h2>
                <div className="space-y-4">
                  <p>
                    Prezentul document este guvernat de legislația din România.
                  </p>
                  <p>
                    Orice dispută apărută în legătură cu utilizarea website-ului va fi soluționată mai întâi pe cale amiabilă.
                  </p>
                  <p>
                    În măsura în care acest lucru nu este posibil, competența revine instanțelor judecătorești competente din România.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  12. Date de contact
                </h2>
                <p>
                  Pentru întrebări privind acești Termeni și Condiții, ne puteți contacta prin intermediul informațiilor disponibile în pagina Contact a website-ului PSY BLOOM Institute.
                </p>
              </div>

              <div className="pt-8 border-t border-border">
                <p className="text-base italic text-muted-foreground">
                  PSY BLOOM Institute este un spațiu dedicat clarificării emoționale, reglării psihologice și înțelegerii mecanismelor care stau la baza anxietății, suprasolicitării, pierderii și dificultăților relaționale, într-un cadru profesional, responsabil și bazat pe practică psihologică autentică.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border bg-[#32506c]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <nav aria-label="Navigare subsol" className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-white/80 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Acasă</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <a href="https://blog.psybloominstitute.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a>
            <Link to="/termeni-si-conditii" className="hover:text-white transition-colors">Termeni și condiții</Link>
            <Link to="/politica-de-confidentialitate" className="hover:text-white transition-colors">Politica de confidentialitate</Link>
            <Link to="/politica-cookies" className="hover:text-white transition-colors">Politica cookies</Link>
          </nav>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/20 text-xs text-white/60">
            <p>© {new Date().getFullYear()} PSY BLOOM Institute</p>
            <p>
              Proiect creat de{" "}
              <a href="https://algoritmii.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Algoritmii.ro
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
